/**
 * billModel — SINGLE SOURCE OF TRUTH for the running-bill ledger projection
 * (C3 / S6). RunningBillPanel (and any future surface that lists charges) reads
 * THIS pure selector; the grouping / reversal-linking / filter / collapse logic
 * lives in ONE place so it can't drift.
 *
 * Balance and totals are NOT computed here — they come straight off the
 * RunningBillResponse (`balance_paise`, `total_charges_paise`,
 * `total_deposits_paise`), the server's cached truth. This model only shapes the
 * LINE list for display (group-by-day + reversal links); it never re-derives the
 * balance (single-source rule — no fake ₹0, no inline re-derive).
 *
 * Input `charges` is the API order (newest SK first). Output day-groups are
 * newest calendar date first.
 */

import {
  CHARGE_KIND,
  CHARGE_FILTER_ALL,
  LEDGER_VISIBLE_DAYS,
  PAYMENT_SOURCE_TYPE,
  PAYMENTS_FILTER_ALL,
  PAYMENTS_VISIBLE_ROWS,
  PAYMENTS_LONG_LIST_THRESHOLD,
  PAYMENT_MODE_LABELS,
  DUES_STATUS,
  PAYER_MODE,
  TPA_BILL_ERROR_CODE,
  DISCHARGE_ERROR_CODE,
  daysAdmitted,
  isAwaitingBed,
  isTerminal,
} from '../../../constants/admission';
import { formatServiceDate, formatServiceDateShort, formatPaise, formatIsoDateOnly } from '../../../utils/money';

/**
 * describeBalance — the SINGLE source that turns the server's cached signed
 * `balance_paise` into a labelled, always-positive display. Consumed by the
 * running-bill header, the totals footer, the desk row and every deposit/refund
 * preview so "Advance held" vs "Balance owed" can never diverge, and a negative
 * balance is NEVER shown as "−₹" (business rule: advance is money held, not owed).
 *
 *   > 0  → { kind:'owed',    label:'Balance owed', text:'₹X'  }
 *   == 0 → { kind:'zero',    label:'Balance owed', text:'₹0'  }
 *   < 0  → { kind:'advance', label:'Advance held', text:'₹Y'  }  (Y = |balance|)
 */
export function describeBalance(balancePaise) {
  const n = Math.round(Number(balancePaise) || 0);
  if (n < 0) return { kind: 'advance', label: 'Advance held', text: formatPaise(-n), paise: n };
  if (n === 0) return { kind: 'zero', label: 'Balance owed', text: formatPaise(0), paise: 0 };
  return { kind: 'owed', label: 'Balance owed', text: formatPaise(n), paise: n };
}

/**
 * buildAdmissionKpis — SINGLE SOURCE for the Variant-B owner-glance KPI strip
 * AND the rail-item balance (UX-1 AC7). Every financial/occupancy value the
 * split-pane shows for ONE admission is derived HERE from the admission row's
 * server-owned denorm fields — never re-derived inline per surface, never a
 * fake ₹0 default. The balance leg reuses `describeBalance` (the same selector
 * the running-bill header + desk chip read), so the rail chip, the KPI strip
 * and the Bill-tab totals can never show a conflicting number.
 *
 * @returns {{
 *   balance: {kind,label,text,paise},   // via describeBalance(balance_paise)
 *   depositsHeldPaise: number,          // net deposits held
 *   bedLabel: string|null,
 *   wardName: string|null,
 *   awaiting: boolean,                  // ADMITTED with no bed (isAwaitingBed)
 *   losDays: number|null,               // whole days since admitted_at
 *   terminal: boolean,
 *   depositLow: boolean,                // server overlay flag only
 *   depositShortfallPaise: number,
 * }}
 */
export function buildAdmissionKpis(admission) {
  const a = admission || {};
  return {
    balance: describeBalance(a.balance_paise),
    depositsHeldPaise: Number(a.deposit_total_paise) || 0,
    bedLabel: a.bed_label || null,
    wardName: a.ward_name || null,
    awaiting: isAwaitingBed(a),
    losDays: daysAdmitted(a),
    terminal: isTerminal(a.status),
    depositLow: !!a.deposit_low,
    depositShortfallPaise: Number(a.deposit_shortfall_paise) || 0,
  };
}

function matchesFilter(charge, filter) {
  if (!filter || filter === CHARGE_FILTER_ALL) return true;
  return charge.kind === filter;
}

function matchesQuery(charge, q) {
  if (!q) return true;
  const hay = `${charge.description || ''} ${charge.reason || ''}`.toLowerCase();
  return hay.includes(q);
}

/**
 * @param {object} args
 * @param {Array}  args.charges   ChargeResponse[] (newest first, signed paise)
 * @param {string} [args.filter]  CHARGE_FILTER key ('ALL' | 'BED_DAY' | 'MANUAL' | 'REVERSAL')
 * @param {string} [args.query]   free-text search over description/reason
 * @param {boolean}[args.showOlder] reveal the collapsed older day-groups
 * @returns {{
 *   groups: Array<{ dateKey, label, subtotalPaise, lines }>,
 *   visibleGroups: Array,        // groups to render now
 *   hiddenDayCount: number,      // collapsed older days (0 when filtering/searching)
 *   hiddenTotalPaise: number,    // Σ of the collapsed days
 *   totalLineCount: number,      // charges before filter/search
 *   matchedLineCount: number,    // charges after filter/search
 *   isFiltering: boolean,        // a chip other than ALL, or a search term, is active
 *   showSearch: boolean,         // long-list search affordance shown (>20 lines)
 * }}
 * Each line = the raw charge + { reversed, isReversal, reversalOfLabel }.
 */
export function buildBillModel({ charges, filter = CHARGE_FILTER_ALL, query = '', showOlder = false } = {}) {
  const all = Array.isArray(charges) ? charges : [];
  const q = (query || '').trim().toLowerCase();
  const isFiltering = (filter && filter !== CHARGE_FILTER_ALL) || q.length > 0;

  // Index by charge_id so a REVERSAL can point back at the original it reverses.
  const byId = new Map();
  all.forEach((c) => { if (c && c.charge_id) byId.set(c.charge_id, c); });

  const matched = all.filter((c) => c && matchesFilter(c, filter) && matchesQuery(c, q));

  // Group by service_date (org-local YYYY-MM-DD), newest date first.
  const groupMap = new Map();
  matched.forEach((c) => {
    const key = c.service_date || '—';
    if (!groupMap.has(key)) groupMap.set(key, []);
    const original = c.reversal_of ? byId.get(c.reversal_of) : null;
    const reversalRow = c.reversed_by ? byId.get(c.reversed_by) : null;
    groupMap.get(key).push({
      ...c,
      reversed: !!c.reversed_by,
      isReversal: c.kind === CHARGE_KIND.REVERSAL,
      reversalOfLabel: original
        ? `${original.description || 'charge'} of ${formatServiceDateShort(original.service_date)}`
        : null,
      reversedOnLabel: reversalRow ? formatServiceDateShort(reversalRow.service_date) : null,
    });
  });

  const groups = Array.from(groupMap.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0)) // desc by YYYY-MM-DD string
    .map(([dateKey, lines]) => ({
      dateKey,
      label: formatServiceDate(dateKey),
      subtotalPaise: lines.reduce((s, l) => s + (Number(l.amount_paise) || 0), 0),
      lines,
    }));

  // Collapse beyond the latest N day-groups — but only when NOT filtering/searching
  // (a filter/search should reveal every match regardless of age).
  let visibleGroups = groups;
  let hiddenDayCount = 0;
  let hiddenTotalPaise = 0;
  if (!isFiltering && !showOlder && groups.length > LEDGER_VISIBLE_DAYS) {
    visibleGroups = groups.slice(0, LEDGER_VISIBLE_DAYS);
    const hidden = groups.slice(LEDGER_VISIBLE_DAYS);
    hiddenDayCount = hidden.length;
    hiddenTotalPaise = hidden.reduce((s, g) => s + g.subtotalPaise, 0);
  }

  return {
    groups,
    visibleGroups,
    hiddenDayCount,
    hiddenTotalPaise,
    totalLineCount: all.length,
    matchedLineCount: matched.length,
    isFiltering,
    showSearch: all.length > 20,
  };
}

/**
 * buildDepositsModel — SINGLE SOURCE for the deposits/refunds list projection
 * (C4 / S5). The RunningBillPanel deposits section reads THIS pure selector: it
 * nests each REFUND under its parent deposit, applies the Deposits/Refunds chip
 * filter + free-text search, collapses beyond the latest N rows, and computes a
 * per-row running "deposits held (net)" for DISPLAY only.
 *
 * It NEVER re-derives the authoritative net total or balance — those come off
 * the RunningBillResponse (`total_deposits_paise`, `balance_paise`). The running
 * column is a cosmetic cumulative of the server's own signed `net_amount_paise`.
 *
 * Input `payments` = PaymentLine[] (deposits + refunds, any order).
 *
 * @returns {{
 *   rows: Array,               // display rows (parent deposit + nested refunds), newest parent first
 *   visibleRows: Array,        // rows to render now (collapse applied when not filtering)
 *   hiddenCount: number,       // collapsed rows (0 when filtering/searching/showAll)
 *   depositCount: number,      // # of deposit (parent) rows, pre-filter
 *   refundCount: number,       // # of refund rows, pre-filter
 *   matchedCount: number,      // rows after filter/search
 *   isFiltering: boolean,
 *   showSearch: boolean,       // long-list search affordance (> threshold rows)
 *   isEmpty: boolean,          // no payments at all
 * }}
 */
export function buildDepositsModel({ payments, filter = PAYMENTS_FILTER_ALL, query = '', showAll = false } = {}) {
  const all = (Array.isArray(payments) ? payments : []).filter((p) => p && p.status !== 'VOIDED');
  const q = (query || '').trim().toLowerCase();
  const isFiltering = (filter && filter !== PAYMENTS_FILTER_ALL) || q.length > 0;

  const deposits = all.filter((p) => p.source_type === PAYMENT_SOURCE_TYPE.IPD_DEPOSIT);
  const refunds = all.filter((p) => p.source_type === PAYMENT_SOURCE_TYPE.REFUND);

  const sortByTimeDesc = (a, b) => String(b.collected_at || '').localeCompare(String(a.collected_at || ''));
  const sortByTimeAsc = (a, b) => String(a.collected_at || '').localeCompare(String(b.collected_at || ''));

  // Running "deposits held (net)" per row — walk chronologically over the signed
  // net of every non-voided payment, then key the cumulative back onto each row.
  const heldAfter = new Map();
  let cumulative = 0;
  [...all].sort(sortByTimeAsc).forEach((p) => {
    cumulative += Number(p.net_amount_paise) || 0;
    heldAfter.set(p.payment_id, cumulative);
  });

  const refundsByParent = new Map();
  refunds.forEach((r) => {
    const key = r.parent_payment_id;
    if (!refundsByParent.has(key)) refundsByParent.set(key, []);
    refundsByParent.get(key).push(r);
  });

  const matchesPaymentQuery = (p) => {
    if (!q) return true;
    const hay = `${p.receipt_number || ''} ${p.method || ''} ${p.reason || ''} ${p.collected_by_role || ''} ${p.notes || ''}`.toLowerCase();
    return hay.includes(q);
  };
  const matchesPaymentFilter = (p) => {
    if (!filter || filter === PAYMENTS_FILTER_ALL) return true;
    return p.source_type === filter;
  };

  // Flatten to display rows: each visible deposit, followed by its nested refunds.
  // The Refunds-only filter shows refund rows standalone (still tagged childOf).
  const rows = [];
  deposits.sort(sortByTimeDesc).forEach((dep) => {
    const kids = (refundsByParent.get(dep.payment_id) || []).sort(sortByTimeAsc);
    const depVisible = matchesPaymentFilter(dep) && matchesPaymentQuery(dep);
    const visibleKids = kids.filter((k) => matchesPaymentFilter(k) && matchesPaymentQuery(k));
    if (depVisible) {
      rows.push({ ...dep, isRefund: false, heldPaise: heldAfter.get(dep.payment_id) ?? null });
      visibleKids.forEach((k) => rows.push({ ...k, isRefund: true, childOf: dep.payment_id, heldPaise: heldAfter.get(k.payment_id) ?? null }));
    } else if (visibleKids.length > 0) {
      // Parent filtered out but a child matches (Refunds filter): show the refund
      // standalone so it's never orphaned/hidden.
      visibleKids.forEach((k) => rows.push({ ...k, isRefund: true, childOf: dep.payment_id, orphaned: true, heldPaise: heldAfter.get(k.payment_id) ?? null }));
    }
  });
  // Orphan refunds whose parent isn't in this partition (defensive) — never drop a refund.
  refunds.forEach((r) => {
    if (deposits.some((d) => d.payment_id === r.parent_payment_id)) return;
    if (!(matchesPaymentFilter(r) && matchesPaymentQuery(r))) return;
    rows.push({ ...r, isRefund: true, orphaned: true, heldPaise: heldAfter.get(r.payment_id) ?? null });
  });

  const matchedCount = rows.length;

  let visibleRows = rows;
  let hiddenCount = 0;
  if (!isFiltering && !showAll && rows.length > PAYMENTS_VISIBLE_ROWS) {
    visibleRows = rows.slice(0, PAYMENTS_VISIBLE_ROWS);
    hiddenCount = rows.length - PAYMENTS_VISIBLE_ROWS;
  }

  return {
    rows,
    visibleRows,
    hiddenCount,
    depositCount: deposits.length,
    refundCount: refunds.length,
    matchedCount,
    isFiltering,
    showSearch: all.length > PAYMENTS_LONG_LIST_THRESHOLD,
    isEmpty: all.length === 0,
  };
}

/** True iff a parent deposit has no refundable amount left (drives disabled Refund). */
export function isFullyRefunded(payment) {
  if (!payment) return false;
  return (Number(payment.refundable_remaining_paise) || 0) <= 0;
}

/**
 * buildRefundableDeposits — SINGLE SOURCE for `DischargeDialog`'s
 * `refundableDeposits` prop (C5 · FE-2 contract #11 "N refunds, oldest-first").
 * Filters the bill's `payments[]` down to non-voided deposit PARENTS still
 * holding a refundable remainder, oldest `collected_at` first — so a
 * multi-parent advance walks front-to-back one deposit at a time. Re-run this
 * over a freshly-reloaded bill after every refund (FE-4 mount) so the dialog
 * naturally advances to the next parent.
 */
export function buildRefundableDeposits(payments) {
  const list = Array.isArray(payments) ? payments : [];
  return list
    .filter((p) => p
      && p.source_type === PAYMENT_SOURCE_TYPE.IPD_DEPOSIT
      && p.status !== 'VOIDED'
      && (Number(p.refundable_remaining_paise) || 0) > 0)
    .sort((a, b) => String(a.collected_at || '').localeCompare(String(b.collected_at || '')));
}

/**
 * INSURANCE_BILL_READ_KEYS — the FROZEN CONTRACT C1 read-key set (bk-1323 /
 * TPA-PREAUTH lite-pr FE-1). These are the ONLY 9 keys `FinalBillResponse`
 * carries for the insurer block; `buildInsuranceModel` below reads NOTHING
 * outside this list. Exported so tests (and any future reader) can assert a
 * mock/response never carries a key the contract does not — a removed field
 * must fail a test here, not surface as a silent blank on screen (AC4).
 * Backend mirrors this list byte-for-byte in its own contract test module
 * (`tests/integration/test_final_bill_insurance_contract.py`); cross-repo
 * drift is caught by the CONTRACT RECONCILE integration step, not here.
 */
export const INSURANCE_BILL_READ_KEYS = [
  'payer_mode',
  'insurer_settled_paise',
  'insurance_settlements',
  'insurer_name',
  'policy_no',
  'claim_no',
  'tpa_name',
  'copay_paise',
  'disallowed_paise',
];

/**
 * buildInsuranceIdentity — the ONE place that shapes the insurer/TPA/policy/
 * claim identity + its payer-mode-driven copy (block title, claim label) off
 * a bill payload. Shared by `buildInsuranceModel` (final bill, C-2) and
 * `buildRunningInsuranceModel` (running bill, C1b) below so the identity
 * fields and their labelling can never drift between the two surfaces — a
 * REIMBURSEMENT claim reads "Claim no." on both, not "Claim no." on one and
 * "Pre-auth / claim no." on the other.
 */
function buildInsuranceIdentity(bill) {
  const payerMode = bill?.payer_mode || PAYER_MODE.NONE;
  return {
    payerMode,
    insurerName: bill?.insurer_name || null,
    policyNo: bill?.policy_no || null,
    claimNo: bill?.claim_no || null,
    tpaName: bill?.tpa_name || null,
    showClaimBlock: payerMode !== PAYER_MODE.NONE,
    blockTitle: payerMode === PAYER_MODE.REIMBURSEMENT ? 'Claim information · reimbursement' : 'Cashless / TPA details',
    claimLabel: payerMode === PAYER_MODE.REIMBURSEMENT ? 'Claim no.' : 'Pre-auth / claim no.',
  };
}

/**
 * buildInsuranceModel — SINGLE SOURCE for the on-screen insurer-deduction
 * presentation (FE-2 · C-2 frozen-contract additive fields:
 * `insurer_settled_paise` / `payer_mode` / `insurer_name` / `policy_no` /
 * `claim_no` / `tpa_name` / `copay_paise` / `disallowed_paise`). Reads these
 * straight off the final-bill response — NEVER re-derives the ladder math;
 * `net payable` stays the server's own `balance_paise` (already nets advance
 * + insurer_settled server-side, INV-1). The insurer line here is a
 * DISPLAY-only settlement row, peer of the deposits "held (net)" figure,
 * never folded into `net_charges_paise` (no pre-tax discount, INV-2). Co-pay
 * + disallowed render as ONE italic reason note — never a second deduction
 * row (no double-count).
 */
export function buildInsuranceModel(bill) {
  const identity = buildInsuranceIdentity(bill);
  const insurerSettledPaise = Number(bill?.insurer_settled_paise) || 0;
  const copayPaise = Number(bill?.copay_paise) || 0;
  const disallowedPaise = Number(bill?.disallowed_paise) || 0;
  const showInsurerDeduction = identity.payerMode === PAYER_MODE.CASHLESS && insurerSettledPaise > 0;

  // TWO distinct reason lines (owner-locked two-field patient-borne) — mirrors
  // BE-5's canonical PDF form (`_reason_lines_html`): co-pay and disallowed on
  // SEPARATE lines, each shown only if nonzero (no "₹0" noise). Display-only —
  // the residual already folds these in (never a second deduction, INV-2).
  const reasonLines = [];
  if (showInsurerDeduction && copayPaise > 0) {
    reasonLines.push(`Co-pay (policy share): ${formatPaise(copayPaise)}`);
  }
  if (showInsurerDeduction && disallowedPaise > 0) {
    reasonLines.push(`Disallowed (non-payable items): ${formatPaise(disallowedPaise)}`);
  }

  return {
    ...identity,
    insurerSettledPaise,
    showInsurerDeduction,
    reasonLines,
    reimbursementNote: identity.payerMode === PAYER_MODE.REIMBURSEMENT
      ? 'Patient pays the full amount below. This information is provided so the patient can file their own reimbursement claim with the insurer — it does not reduce the amount payable here.'
      : null,
  };
}

/**
 * INSURANCE_RUNNING_READ_KEYS — the frozen C1b `RunningBillResponse` additive
 * key set `buildRunningInsuranceModel` below is allowed to read. Mirrored by
 * the backend contract test (`test_fe_running_read_keys_subset_of_running_bill_schema`)
 * so a field can never be renamed/dropped on one side without failing CI.
 * Deliberately EXCLUDES `insurer_settled_paise` / `insurance_settlements` —
 * C1b never puts those on the running bill (they stay 0 until the
 * discharge-initiation mint); reading them here would show a fact that isn't
 * true yet.
 */
export const INSURANCE_RUNNING_READ_KEYS = [
  'payer_mode',
  'insurer_name',
  'tpa_name',
  'policy_no',
  'claim_no',
  'insurer_approved_paise',
  'copay_paise',
  'disallowed_paise',
];

/**
 * buildRunningInsuranceModel — SINGLE SOURCE for the IDENTITY-ONLY claim
 * block on the RUNNING bill (A1 · C1b, `INSURANCE_RUNNING_READ_KEYS` above).
 * Shares `buildInsuranceIdentity` with the final-bill selector so the
 * insurer/TPA/policy/claim fields and their payer-mode copy can never diverge
 * between the live view and the locked one.
 *
 * Deliberately NOT a deduction row: `insurer_approved_paise` is shown as
 * INFORMATION (approved-so-far), never subtracted from anything on screen —
 * the running-bill ladder (`balance_paise`) is untouched by this block
 * (owner ruling A1: the deduction line stays final-view only, once
 * `insurer_settled_paise` mints at discharge-initiation). `showApprovedAmount`
 * is false at exactly ₹0 so a stay with no approval yet never reads "₹0
 * approved" as a fact — identity renders alone until a real figure exists.
 */
export function buildRunningInsuranceModel(bill) {
  const identity = buildInsuranceIdentity(bill);
  const insurerApprovedPaise = Number(bill?.insurer_approved_paise) || 0;
  return {
    ...identity,
    insurerApprovedPaise,
    showApprovedAmount: identity.showClaimBlock && insurerApprovedPaise > 0,
  };
}

/**
 * buildFinalBillModel — SINGLE SOURCE for the FINAL-BILL projection (C5 ·
 * FE-3, contract #5 `GET /final-bill`). Keeps the three money columns
 * structurally separate so they can never cross on screen:
 *   - `chargesModel` = day-grouped POSTED charges (`bill.charges`, kind !=
 *     CONCESSION) via the SAME `buildBillModel` selector the running bill uses.
 *   - `concessionLines` = `bill.concessions` ONLY (never mixed into charges or
 *     deposits) — always voucher-labelled (BE-5's assembler stamps
 *     `voucher_number` on every concession line; contract reconcile dropped
 *     the old description fallback).
 *   - `depositsAdapter` = `bill.deposits` + `bill.refunds` shaped for the
 *     existing `DepositsSection`/`buildDepositsModel` (a return-of-advance
 *     stays a REFUND row here, never a charges credit).
 *   - `insurance` = the FE-2 insurer-deduction/claim-block projection, via
 *     `buildInsuranceModel` above (single source, never re-derived inline).
 * Dues footer reads `bill.dues_paise` ONLY — never re-derived from
 * `balance_paise` (a dues-type discharge can complete with balance == dues,
 * but the footer must show the STAMPED figure, not a recompute).
 */
export function buildFinalBillModel({ bill, filter = CHARGE_FILTER_ALL, query = '', showOlder = false } = {}) {
  const chargesModel = buildBillModel({ charges: bill?.charges, filter, query, showOlder });
  const concessionLines = (bill?.concessions || []).map((c) => ({
    ...c,
    description: `Concession / waiver — voucher ${c.voucher_number || '—'}`,
  }));
  const depositsAdapter = {
    payments: [...(bill?.deposits || []), ...(bill?.refunds || [])],
    deposit_total_paise: bill?.deposit_gross_paise,
    refunded_deposits_paise: bill?.refund_total_paise,
    total_deposits_paise: bill?.net_deposits_paise,
  };
  const duesPaise = Number(bill?.dues_paise) || 0;
  const hasDues = bill?.dues_paise != null && duesPaise > 0;
  const balance = describeBalance(bill?.balance_paise);
  const insurance = buildInsuranceModel(bill);
  return { chargesModel, concessionLines, depositsAdapter, hasDues, duesPaise, balance, insurance };
}

/**
 * buildDuesAddendumModel — SINGLE SOURCE for the READ-ONLY dues-settlement
 * addendum under a locked final bill (C6b · FE-6, copy-deck §5.2, contract §5
 * additive fields: `dues_collections`, `dues_paise` [remaining], `dues_status`,
 * `dues_collected_paise` [cumulative], `written_off_paise`). The bill face
 * itself is UNCHANGED (same bill_number/bill_rev) — this only renders the
 * post-discharge collections/write-off history under it. NO collect/write-off
 * affordance is ever rendered from this model or its caller (owner ruling 2 —
 * the final-bill panel stays read-only; Collect/Write-off live only on the
 * queue row + admission detail, C6b FE-2/FE-5).
 *
 * `originalDuesPaise` (the "dues of ₹X recorded on this bill" intro figure) is
 * NOT a field the frozen contract carries directly — it is DERIVED as
 * collected + remaining + written-off (the three ways the original recorded
 * due can only ever be accounted for; contract-silent, flagged for the
 * tech-lead's contract reconcile against the built backend).
 *
 * Visible only when there is evidence dues ever existed on this admission
 * (remaining > 0, or any collected/written-off/collection-list history) —
 * never shown on a bill that never carried dues.
 */
export function buildDuesAddendumModel(bill) {
  const duesPaise = Number(bill?.dues_paise) || 0; // remaining
  const collectedPaise = Number(bill?.dues_collected_paise) || 0; // cumulative
  const writtenOffPaise = Number(bill?.written_off_paise) || 0;
  const collections = Array.isArray(bill?.dues_collections) ? bill.dues_collections : [];
  const status = bill?.dues_status || null;

  const hadDues = duesPaise > 0 || collectedPaise > 0 || writtenOffPaise > 0 || collections.length > 0;
  if (!hadDues) return { visible: false };

  const originalDuesPaise = collectedPaise + duesPaise + writtenOffPaise;

  const collectionLines = collections.map((c) => ({
    key: c.payment_id || `${c.receipt_number}-${c.collected_at}`,
    text: `${formatPaise(c.net_amount_paise != null ? c.net_amount_paise : c.amount_paise)} received · ${formatIsoDateOnly(c.collected_at)} · ${PAYMENT_MODE_LABELS[c.method] || c.method || '—'} · receipt ${c.receipt_number || '—'}`,
  }));

  // Write-off authoriser name/date are NOT on the frozen contract's final-bill
  // additive fields (only `written_off_paise`) — render whichever of
  // date/authoriser the bill happens to carry and omit the rest gracefully
  // (contract-silent; same graceful-degrade as the admission-detail state,
  // flagged for reconcile).
  let writeOffLine = null;
  if (status === DUES_STATUS.WRITTEN_OFF && writtenOffPaise > 0) {
    const date = formatIsoDateOnly(bill?.dues_written_off_at || bill?.written_off_at);
    const who = bill?.written_off_by_name || bill?.written_off_by_role || null;
    writeOffLine = `${formatPaise(writtenOffPaise)} written off as uncollectable`
      + (date ? ` · ${date}` : '')
      + (who ? ` · authorised by ${who}` : '');
  }

  let closingLine;
  if (status === DUES_STATUS.WRITTEN_OFF) closingLine = `Remaining due: ₹0 — ${formatPaise(writtenOffPaise)} written off.`;
  else if (duesPaise === 0) closingLine = 'Remaining due: ₹0 — settled.';
  else closingLine = `Remaining due: ${formatPaise(duesPaise)}.`;

  return {
    visible: true,
    heading: 'Dues settlement (after discharge)',
    introLine: `The dues of ${formatPaise(originalDuesPaise)} recorded on this bill were settled as follows. The bill above is unchanged.`,
    collectionLines,
    writeOffLine,
    closingLine,
  };
}

/**
 * buildDiagWaiverLine — SINGLE SOURCE for the recorded-diagnostic-waiver
 * display (DIAG-DISCOUNT v5 FE-G, copy §13.10). The running bill, the final
 * bill AND the Diagnostics tab all read the SAME `diag_waiver_*` scalars +
 * `lab_discounts[]` off the bill (BE-F, contract §1.4) through this ONE
 * selector, so the recorded figure, its status wording and the who/when
 * attribution can never diverge between surfaces — this is the fix for the
 * defect the owner reported ("he records a waiver and nothing on any screen
 * confirms it exists").
 *
 * `remaining_paise` is READ off `bill.diag_waiver_remaining_paise` — the
 * contract prose (§1.1/§1.4) reads as naming only the three scalars +
 * `lab_discounts[]`, but that reading was stale: the shipped schema
 * (`RunningBillResponse`/`FinalBillResponse`, `app/schemas/admission_charge.py`)
 * DOES carry `diag_waiver_remaining_paise` on both bill reads, server-derived
 * via the ONE shared `diag_waiver_amounts` reader, which also subtracts a
 * `lapsed` term (a discharged stay whose waiver lapsed still owes ₹0, not the
 * naive `recorded - reversed - applied`). Client must NEVER re-derive this —
 * a local formula can only drift from the server's, and it did (a lapsed
 * waiver showed a stale "remaining" here). If the field is absent (e.g. a
 * stale cached payload), the line reads no remaining rather than computing
 * one — no silent fallback formula.
 *
 * `visible: false` when nothing has ever been recorded — the caller MUST
 * render no line at all then (never a fabricated ₹0 row, §13.10 `.none`).
 */
export function buildDiagWaiverLine(bill) {
  const recordedPaise = Math.round(Number(bill?.diag_waiver_recorded_paise) || 0);
  if (recordedPaise <= 0) return { visible: false };
  const reversedPaise = Math.round(Number(bill?.diag_waiver_reversed_paise) || 0);
  const appliedPaise = Math.round(Number(bill?.diag_waiver_applied_paise) || 0);
  const remainingPaise = bill?.diag_waiver_remaining_paise != null
    ? Math.round(Number(bill.diag_waiver_remaining_paise) || 0)
    : 0;

  // Attribution/reason/reversed-stamp: BE-F names only the scalars +
  // `lab_discounts[]` on the bill read (contract §1.4) — no dedicated
  // recorded-by/date scalar. Sourced from the append-only LABDISC rows —
  // row shape VERIFIED against the shipped `LabDiscountLine` schema
  // (`app/schemas/admission_charge.py`): `kind ∈ {RECORD, REVERSAL}`,
  // `amount_paise`, `reason`, `reverses`, `recorded_by{member_id, name,
  // by_system}`, `created_at` — matches this file's original assumption
  // exactly. Ordering is STILL contract-silent, and the shipped assembler
  // (`IPDChargeService._get_running_bill`) sorts the array by `waiver_id`
  // descending, not `created_at` — so taking the MOST RECENT row of each
  // kind by `created_at` explicitly (rather than trusting array order)
  // stays correct and is kept as-is.
  const discounts = Array.isArray(bill?.lab_discounts) ? bill.lab_discounts : [];
  const latestOfKind = (kind) => discounts
    .filter((e) => e && e.kind === kind)
    .sort((a, b) => String(b?.created_at || '').localeCompare(String(a?.created_at || '')))[0] || null;
  const recordEntry = latestOfKind('RECORD');
  const reversalEntry = latestOfKind('REVERSAL');

  let status;
  if (remainingPaise === 0 && appliedPaise > 0) status = 'fullyApplied';
  else if (remainingPaise === 0 && reversedPaise > 0) status = 'reversed';
  else if (appliedPaise > 0) status = 'partly';
  else status = 'pending'; // the default state, and the one the owner was missing

  return {
    visible: true,
    status,
    recordedPaise,
    reversedPaise,
    appliedPaise,
    remainingPaise,
    reason: recordEntry?.reason || null,
    recordedByName: recordEntry?.recorded_by?.name || null,
    recordedAtLabel: recordEntry?.created_at ? formatIsoDateOnly(recordEntry.created_at) : null,
    reversedByName: reversalEntry?.recorded_by?.name || null,
    reversedAtLabel: reversalEntry?.created_at ? formatIsoDateOnly(reversalEntry.created_at) : null,
  };
}

/**
 * tpaBillErrorMessage — plain, desk-readable copy for every machine code the
 * three TPA-Bill routes raise (C2), verified against the BUILT backend at the
 * contract reconcile. ONE map: the issue button (`RunningBillPanel`, both
 * views) and the per-row Reprint (`TpaBillSection`) both read it, so the same
 * failure can never be described two ways. The server `message` is never
 * shown — no UI strings on the wire.
 *
 * Returns `null` for an unknown code so the caller keeps its own fallback
 * sentence (the same shape `dischargeErrorMessage` uses).
 */
export function tpaBillErrorMessage(code) {
  switch (code) {
    case TPA_BILL_ERROR_CODE.TPA_BILL_INSURER_REQUIRED:
      return 'Add the insurer and TPA name on the insurance card before issuing a TPA bill.';
    case TPA_BILL_ERROR_CODE.NOT_CASHLESS:
      return 'A TPA bill can only be issued for a cashless admission. Change the claim mode on the insurance card first.';
    case TPA_BILL_ERROR_CODE.ISSUE_AFTER_COMPLETION:
      return 'This admission is complete, so no new TPA bill can be issued. Bills already issued can still be reprinted.';
    case TPA_BILL_ERROR_CODE.SNAPSHOT_TOO_LARGE:
      return 'This admission has too many charge lines to fit on one TPA bill.';
    case TPA_BILL_ERROR_CODE.TPA_BILL_NOT_FOUND:
      return 'That TPA bill is no longer available. Refresh the page and try again.';
    case DISCHARGE_ERROR_CODE.ADMISSION_NOT_FOUND:
      return 'This admission could not be found. Refresh the page and try again.';
    default:
      return null;
  }
}
